import { Component, DestroyRef, inject, Input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { BaseModalComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { CreateLotItemForm } from '@app/models/inventory/lot-item-forms';
import { CHEMICAL_LOT_PACKAGING_TYPES, PackagingType } from '@app/models/inventory/packaging-type';
import { LotItem } from '@app/models/inventory/requisition';
import { BatchLotItemProxyService } from '@app/proxies/inventory/batch-lot-item.proxy.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { grossNotBelowNetValidator } from '@app/validators/lot-item/gross-not-below-net';

const DEFAULT_LOCATION_CODE = 'PC.WH-PROD-1';

@Component({
  selector: 'app-create-lot-item-modal',
  templateUrl: './create-lot-item-modal.component.html',
  styleUrl: './create-lot-item-modal.component.scss',
})
export class CreateLotItemModalComponent extends BaseModalComponent<LotItem | null> {
  @Input() batchId!: string;
  @Input() productDevelopmentNumber!: string;

  private readonly fb = inject(FormBuilder);
  private readonly batchLotItemProxyService = inject(BatchLotItemProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly destroyRef = inject(DestroyRef);

  isLoading = signal(0);

  readonly packagingOptions = CHEMICAL_LOT_PACKAGING_TYPES.map((option) => ({
    value: option.value,
    label: this.localeHelperService.translateWithFallback(option.labelKey, option.fallback),
  }));

  form = this.fb.group<CreateLotItemForm>(
    {
      packagingType: this.fb.nonNullable.control(PackagingType.IbcTote, Validators.required),
      netQuantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
      grossQuantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
    },
    { validators: grossNotBelowNetValidator() },
  );

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const values = this.form.getRawValue();
    this.incrementLoading();

    this.batchLotItemProxyService
      .createLotItem(this.batchId, {
        productDevelopmentNumber: this.productDevelopmentNumber,
        netQuantity: values.netQuantity!,
        grossQuantity: values.grossQuantity!,
        packagingType: values.packagingType,
        currentLocationCode: DEFAULT_LOCATION_CODE,
      })
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ data }) => {
          if (data) {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
              this.localeHelperService.translateWithFallback('CreateLotItem.created', `Lot item ${data.number} created`, {
                number: data.number,
              }),
            );
            this.close({ action: 'save', data });
          }
        },
      });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }
}
